/**
 * Facilities, Entrega 4 (ADR 0027): materiais de consumo e EPI, separados do estoque de produtos.
 * Saldo por item em cada ambiente marcado como estoque, movimentos imutáveis e entrega de EPI ao
 * colaborador. O custo é só informativo (valor da entrada, por moeda).
 */
import { z } from 'zod';

/** Lista provisória (proposta das Entregas 4 a 7): consumo, EPI e resina 3D. */
export const MATERIAL_CATEGORIES = ['consumable', 'ppe', 'resin_3d'] as const;
export type MaterialCategory = (typeof MATERIAL_CATEGORIES)[number];

/** Lista provisória de unidades; muda quando a área confirmar. */
export const MATERIAL_UNITS = [
  'unit',
  'box',
  'pack',
  'pair',
  'roll',
  'liter',
  'kilogram',
  'meter',
] as const;
export type MaterialUnit = (typeof MATERIAL_UNITS)[number];

/** Movimentos lançados à mão (a saída de EPI nasce da entrega ao colaborador). */
export const MATERIAL_MANUAL_KINDS = [
  'entry',
  'transfer',
  'adjustment_in',
  'adjustment_out',
] as const;
/** Movimentos que nascem de outro registro: entrega de EPI, consumo na OS, entrega do pedido. */
export const MATERIAL_MOVEMENT_KINDS = [
  ...MATERIAL_MANUAL_KINDS,
  'ppe_delivery',
  'work_order',
  'work_order_return',
  'request_delivery',
] as const;

/** Categoria pedida por cada modalidade de material (Entrega 4b). */
export const MATERIAL_SERVICE_CATEGORIES: Record<string, MaterialCategory> = {
  F04: 'consumable',
  F15: 'ppe',
  F16: 'resin_3d',
};
export type MaterialMovementKind = (typeof MATERIAL_MOVEMENT_KINDS)[number];

/** Saldo abaixo do mínimo (quantidades em texto decimal, como vêm do banco). */
export function belowMinimum(quantity: string, minQuantity: string | null): boolean {
  return minQuantity !== null && Number(quantity) < Number(minQuantity);
}

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const quantity = z
  .string()
  .trim()
  .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Use números, com ponto nos decimais')
  .refine((v) => Number(v) > 0, 'Quantidade maior que zero');
const minimum = z
  .string()
  .trim()
  .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Use números, com ponto nos decimais')
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');

/** Item: código único na empresa; CA só em EPI. */
export const materialItemInput = z
  .object({
    legalEntityId: z.uuid(),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, 'Letras, números, ponto, hífen ou sublinhado'),
    name: z.string().trim().min(1).max(200),
    category: z.enum(MATERIAL_CATEGORIES),
    unit: z.enum(MATERIAL_UNITS),
    caNumber: optional(30),
    notes: optional(2000),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.category === 'ppe' || !v.caNumber, {
    path: ['caNumber'],
    message: 'O CA só vale para EPI',
  });
export type MaterialItemInput = z.infer<typeof materialItemInput>;

/** Estoque mínimo do item num estoque (vazio = sem mínimo). */
export const materialMinimumInput = z.object({
  locationId: z.uuid(),
  minQuantity: minimum,
});

/** Movimento lançado à mão: entrada (com custo e nota opcionais), transferência ou ajuste. */
export const materialMovementInput = z
  .object({
    itemId: z.uuid(),
    kind: z.enum(MATERIAL_MANUAL_KINDS),
    fromLocationId: optionalId,
    toLocationId: optionalId,
    quantity,
    unitCost: z
      .object({
        amount: z
          .string()
          .trim()
          .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais'),
        currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida'),
      })
      .nullable()
      .optional(),
    invoiceNumber: optional(60),
    reason: optional(2000),
  })
  .refine((v) => v.kind === 'transfer' || v.kind.endsWith('_out') || !!v.toLocationId, {
    path: ['toLocationId'],
    message: 'Escolha o estoque de destino',
  })
  .refine((v) => (v.kind !== 'transfer' && !v.kind.endsWith('_out')) || !!v.fromLocationId, {
    path: ['fromLocationId'],
    message: 'Escolha o estoque de origem',
  })
  .refine((v) => v.kind !== 'transfer' || !!v.toLocationId, {
    path: ['toLocationId'],
    message: 'Escolha o estoque de destino',
  })
  .refine((v) => v.kind !== 'transfer' || v.fromLocationId !== v.toLocationId, {
    path: ['toLocationId'],
    message: 'Origem e destino precisam ser diferentes',
  })
  .refine((v) => !v.kind.startsWith('adjustment') || (v.reason?.length ?? 0) >= 5, {
    path: ['reason'],
    message: 'Explique o ajuste (ao menos 5 letras)',
  })
  .refine((v) => v.kind === 'entry' || (!v.unitCost && !v.invoiceNumber), {
    path: ['unitCost'],
    message: 'Custo e nota fiscal só na entrada',
  });
export type MaterialMovementInput = z.infer<typeof materialMovementInput>;

/** Entrega de EPI a um colaborador (CA do item quando vazio). */
export const ppeDeliveryInput = z.object({
  itemId: z.uuid(),
  employeeId: z.uuid(),
  fromLocationId: z.uuid(),
  quantity,
  caNumber: optional(30),
  deliveredOn: isoDate,
  notes: optional(2000),
});
export type PpeDeliveryInput = z.infer<typeof ppeDeliveryInput>;

/** Itens do pedido de material, enquanto o chamado é rascunho (substitui a lista toda). */
export const requestMaterialsInput = z
  .object({
    lines: z.array(z.object({ itemId: z.uuid(), quantity })).max(50),
  })
  .refine((v) => new Set(v.lines.map((l) => l.itemId)).size === v.lines.length, {
    path: ['lines'],
    message: 'Cada item entra uma vez só',
  });
export type RequestMaterialsInput = z.infer<typeof requestMaterialsInput>;

/** Entrega de um item do pedido: do estoque escolhido; EPI vai ao colaborador, com o CA. */
export const requestDeliveryInput = z.object({
  fromLocationId: z.uuid(),
  quantity,
  employeeId: optionalId,
  caNumber: optional(30),
  deliveredOn: isoDate.optional(),
});
export type RequestDeliveryInput = z.infer<typeof requestDeliveryInput>;
